import React, { useState, useEffect } from "react";
import { DragUpdate } from "react-beautiful-dnd";
import {
  editCutsheetData,
  getCutsheetData,
  uploadCutsheetImage,
} from "../../../api/DzoneApi";
import getCutsheetImage from "./cutsheetImage";
import {
  showFilters,
  reorder,
  hideFilters,
  cutsheetDataChange,
  removeIdFieldFromCutsheets,
  formatImageData,
} from "./cutsheet.biz";

type TypeOfData = "Head Jamb" | "Sill" | "Jamb Leg" | "Sidelite Sill" | "";

type DoorType = "Bi-fold" | "Fixed Panel" | "Swing" | "Pivot" | "Slider" | "";

export interface FilterObj {
  typeOfData?: TypeOfData;
  doorType?: DoorType;
  material?: string;
  screen?: string;
  screenPocket?: string;
  screenLayout?: string;
  sill?: string;
  swingDirection?: string;
  swingType?: string;
  sidelite?: string;
  jamb?: string;
  screenType?: string;
  config?: string;
  handle?: string;
  stiles?: string;
  hinge?: string;
  sideliteSide?: string;
}

export type EntryDataDoc = {
  _id: string;
  qty: number;
  part: string;
  species: string;
  width: string;
  height: string;
  length?: string;
};

export type ImageData = {
  publicId: string;
  uploadResult: string;
  metadata: { width: number; height: number; type: string };
};

type ReactSelectChange = React.ChangeEvent<HTMLSelectElement>;

type ReactInputChange = React.ChangeEvent<HTMLInputElement>;

function useCutsheetSelector() {
  const [data, setData] = useState<EntryDataDoc[]>([]);

  const [cutsheetId, setCutsheetId] = useState("");

  const [typeOfData, setTypeOfData] = useState<TypeOfData>("");

  const [doorType, setDoorType] = useState<DoorType>("");

  const [fetching, setFetching] = useState(false);

  //used to add _ids for newly added parts that dont have a mongo id yet
  const [nextId, setNextId] = useState(1);

  const [filter, setFilter] = useState<FilterObj>({});

  const [cleanedFilterObj, setCleanedFilterObj] = useState<FilterObj>({});

  const [changesMade, setChangesMade] = useState(false);

  const [imageData, setImageData] = useState<ImageData>({
    uploadResult: "",
    publicId: "",
    metadata: { width: 0, height: 0, type: "" },
  });

  const handleDoorType = async (event: ReactSelectChange) => {
    const { value } = event.target;
    setDoorType(value as DoorType);

    const newFilters = showFilters(value as string, typeOfData);
    setFilter(newFilters);
    const newObj = {
      ...newFilters,
      doorType: value as DoorType,
      typeOfData,
    };
    await handleFetchData(newObj);
  };

  const handleTypeOf = async (event: ReactSelectChange) => {
    const { value } = event.target;
    setTypeOfData(value as TypeOfData);

    const newFilters = showFilters(doorType as string, value);
    setFilter(newFilters);
    setCleanedFilterObj(newFilters);
    const newObj = {
      ...newFilters,
      typeOfData: value as TypeOfData,
      doorType,
    };
    await handleFetchData(newObj);
  };

  const handleFilterChange = async (
    event: ReactSelectChange | ReactInputChange,
    key: string,
  ) => {
    const { target } = event;
    const value =
      target.type === "checkbox"
        ? (target as HTMLInputElement).checked
        : target.value;
    const updatedFilterObj = { ...filter, [key]: value };
    const cleanedFilterObjectForApi = cleanFilterObj(
      updatedFilterObj,
      doorType,
    );
    if (
      doorType === "Swing" &&
      updatedFilterObj.hinge &&
      updatedFilterObj.sideliteSide &&
      updatedFilterObj.sidelite === "Single" &&
      updatedFilterObj.stiles === "Pico" &&
      updatedFilterObj.screenType === "Centor"
    ) {
      if (
        (updatedFilterObj.hinge === "RH" &&
          updatedFilterObj.sideliteSide === "Left") ||
        (updatedFilterObj.hinge === "LH" &&
          updatedFilterObj.sideliteSide === "Right")
      ) {
        cleanedFilterObjectForApi["isSideliteLockSide"] = true;
      } else {
        cleanedFilterObjectForApi["isSideliteLockSide"] = false;
      }
    }
    setFilter(updatedFilterObj);
    setCleanedFilterObj(cleanedFilterObjectForApi);
    await handleFetchData({
      ...cleanedFilterObjectForApi,
      typeOfData,
      doorType,
    });
  };

  const cleanFilterObj = (filterObj: any, doorType: string) => {
    const filterObjCopy = { ...filterObj };
    if (doorType === "Swing") {
      if (filterObjCopy.swingDirection === "Inswing") {
        delete filterObjCopy.screen;
        delete filterObjCopy.screenType;
        delete filterObjCopy.screenPocket;
        delete filterObjCopy.screenLayout;
      }

      if (filterObjCopy.swingDirection === "Outswing") {
        delete filterObjCopy.jamb;
      }

      if (
        filterObjCopy.sidelite === "none" ||
        filterObjCopy.sidelite === "Double"
      ) {
        delete filterObjCopy.sideliteSide;
      }

      if (filterObjCopy.typeOfData === "Sidelite Sill") {
        if (filterObjCopy.screen === false) {
          delete filterObjCopy.screenType;
          delete filterObjCopy.screenPocket;
          delete filterObjCopy.screenLayout;
        }
      }
    }
    return filterObjCopy;
  };

  const handleFetchData = async (filterObj: FilterObj) => {
    try {
      setFetching(true);
      const res = await getCutsheetData(filterObj);
      setData(res.data?.data || []);
      setCutsheetId(res.data.id);
    } catch (error) {
      console.log(error);
      setData([]);
      setCutsheetId("");
    } finally {
      setFetching(false);
      setChangesMade(false);
    }
  };

  const addEntry = () => {
    if (nextId >= 30) return;

    const entry: EntryDataDoc = {
      _id: String(nextId),
      qty: 1,
      part: "",
      species: "wood",
      width: "",
      height: "",
    };
    setData((prev) => {
      prev.push(entry);
      return prev;
    });
    setNextId((prev) => prev + 1);
  };

  const removeEntry = () => {
    const prev = data.slice(0);
    prev.pop();
    setData(prev);
    setChangesMade(true);
  };

  const submitChanges = async () => {
    if (!changesMade)
      return alert("No updates to make: new data matches old data");

    setFetching(true);
    const { cutsheet, emptyFields } = removeIdFieldFromCutsheets(data);

    if (emptyFields) {
      setFetching(false);
      return alert("Please fill out all fields");
    }

    try {
      const res = await editCutsheetData({
        cutsheetId,
        doorType,
        typeOfData,
        data: cutsheet,
      });
      if (res.data === "updated") {
        setFetching(false);
        return alert("Data successfully updated");
      }
    } catch (err) {
      console.error(err);
      setFetching(false);
    }
  };

  useEffect(() => {
    if (fetching) return;

    if (!doorType || !typeOfData) return;

    (async function fetchImg() {
      const imgUrl = await getCutsheetImage(
        doorType,
        typeOfData,
        cleanedFilterObj,
      );

      const img = document.getElementById("cs-img") as HTMLImageElement;

      if (!img || !imgUrl) {
        return setImageData(formatImageData("", 0, 0, "unknown", ""));
      }

      img.onload = () => {
        setImageData(
          formatImageData(
            imgUrl.split("upload/")[1],
            img.naturalWidth,
            img.naturalHeight,
            imgUrl.split(".").pop() || "unknown",
            "",
          ),
        );
      };

      img.onerror = (error) => {
        console.error("Error loading image:", error);
        setImageData(formatImageData("", 0, 0, "unknown", ""));
      };

      img.src = imgUrl; // Triggers the image to load
    })();
  }, [fetching]);

  const onDragEnd = (result: DragUpdate) => {
    if (
      !result.destination ||
      !data.length ||
      result.source.index === result.destination.index
    )
      return;

    const newOrder = reorder(
      data,
      result.source.index,
      result.destination.index,
    );

    setData(newOrder);
  };

  const handleChange = (event: ReactInputChange, id: string, key: string) => {
    const { value } = event.target;
    setData(cutsheetDataChange(data, id, key, value));
    setChangesMade(true);
  };

  const uploadNewCutsheetImage = async (event: ReactInputChange) => {
    const file = event.target?.files ? event.target.files[0] : null;
    if (!file || !imageData.publicId) return;
    setFetching(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("public_id", imageData.publicId);

    try {
      const res = await uploadCutsheetImage(formData);
      if (res.data === "can not upload in dev env") {
        return;
      }
      const img = document.getElementById("cs-img") as HTMLImageElement;
      img.onload = () => {
        setImageData(
          formatImageData(
            imageData.publicId,
            img.naturalWidth,
            img.naturalHeight,
            res.data.secure_url.split(".").pop() || "unknown",
            "success",
          ),
        );
      };

      img.onerror = (error) => {
        console.error("Error loading image:", error);
        setImageData(
          formatImageData(imageData.publicId, 0, 0, "unknown", "error"),
        );
      };

      img.src = res.data.secure_url;
    } catch (err) {
      console.log(err);
    } finally {
      setImageData(
        formatImageData(imageData.publicId, 0, 0, "unknown", "error"),
      );
      setFetching(false);
    }
  };

  const hiddenFilters = hideFilters(doorType, typeOfData, filter);

  const typeOfDataArr =
    doorType === "Swing"
      ? ["Head Jamb", "Jamb Leg", "Sill", "Sidelite Sill"]
      : ["Head Jamb", "Jamb Leg", "Sill"];

  return {
    typeOfDataArr,
    hiddenFilters,
    uploadNewCutsheetImage,
    handleChange,
    onDragEnd,
    submitChanges,
    removeEntry,
    addEntry,
    handleFilterChange,
    handleTypeOf,
    handleDoorType,
    data,
    imageData,
    typeOfData,
    doorType,
    fetching,
    filter,
  };
}

export default useCutsheetSelector;
